import { useEffect, useState } from 'react';
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  userManagementAPI,
  type User,
  type Branch,
} from '@/services/user-management';
import { toast } from 'sonner';
import { UserDeleteModal } from '@/components/modals/UserDeleteModal';

interface UserQueryParams {
  search?: string;
  role?: string;
  status?: string;
}

const useUsers = ({
  search = '',
  role = 'all',
  status = 'all',
}: UserQueryParams) => {
  return useQuery<User[], Error>({
    queryKey: ['users', { search, role, status }],
    queryFn: () => userManagementAPI.getUsers({ search, role, status }),
  });
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'bg-blue-100 text-blue-800';
    case 'owner':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatRole = (role: string) => {
  return role.charAt(0).toUpperCase() + role.slice(1);
};

export function UserManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  interface UserForDeletion {
    id: string;
    name: string;
    username: string;
    role: 'superadmin' | 'branch_admin' | 'owner';
  }

  const [userToDelete, setUserToDelete] = useState<UserForDeletion | null>(
    null
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const {
    data: users = [],
    isLoading,
    error,
  } = useUsers({
    search: debouncedSearchTerm,
    role: roleFilter === 'all' ? '' : roleFilter,
    status: statusFilter === 'all' ? '' : statusFilter,
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userManagementAPI.deleteUser(userId),
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    },
  });

  const handleDeleteClick = (user: User) => {
    setUserToDelete({
      id: user.id.toString(), // Ensure ID is a string
      name: `${user.firstName} ${user.lastName}`,
      username: user.username,
      role: user.roles.includes('superadmin')
        ? 'superadmin'
        : user.roles.includes('owner')
          ? 'owner'
          : 'branch_admin',
    });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id);
    }
  };

  // const { data: branchStats, isLoading: isLoadingBranchStats } = useQuery({
  //   queryKey: ["branchStats"],
  //   queryFn: () => userManagementAPI.getBranchStats(),
  // });

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="h-24 text-center">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="h-24 text-center text-red-600">
            Error loading users. Please try again later.
          </TableCell>
        </TableRow>
      );
    }

    if (users.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="h-24 text-center text-gray-500">
            No users found matching your criteria.
          </TableCell>
        </TableRow>
      );
    }

    return users.map((user: User) => (
      <TableRow key={user.uid}>
        <TableCell className="font-medium">
          <div className="flex items-center space-x-3">
            <Avatar>
              <AvatarFallback>
                {user.firstName[0]}
                {user.lastName[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">
                {user.firstName} {user.lastName}
              </div>
              <div className="text-sm text-gray-500">@{user.username}</div>
            </div>
          </div>
        </TableCell>
        <TableCell>{user.phoneNumber}</TableCell>
        <TableCell>
          <div className="flex flex-wrap gap-1">
            {user.roles.map((role: string) => (
              <Badge key={role} className={getRoleColor(role)}>
                {formatRole(role)}
              </Badge>
            ))}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-col space-y-1">
            {user.branches.map((branch: Branch) => (
              <div key={branch.id} className="text-sm">
                {branch.name}{' '}
                <span className="text-xs text-gray-500">({branch.role})</span>
              </div>
            ))}
          </div>
        </TableCell>
        <TableCell>{format(new Date(user.createdAt), 'MMM d, yyyy')}</TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate(`/user-management/${user?.id}`)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem>
                <MessageCircle className="h-4 w-4 mr-2" />
                Start Chat
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(`/user-management/edit/${user?.id}`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit User
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(user);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end space-y-4 md:flex-row md:items-center md:space-y-0">
        <div className="flex items-center justify-end">
          <Button onClick={() => navigate('/user-management/add')}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>
      {/* <UserStatsCards
        totalUsers={users.length}
        superAdmins={users.filter((u) => u.role === "superadmin").length}
        branchAdmins={users.filter((u) => u.role === "branch_admin").length}
        activeChats={users.filter((u) => u.status === "active").length}
      /> */}

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col justify-between space-y-4 md:flex-row md:items-center md:space-y-0">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row justify-between sm:space-x-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="hidden">
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="superadmin">Super Admin</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-md border ">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Branches</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderTableContent()}</TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <UserDeleteModal
        user={userToDelete}
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteUserMutation.isPending}
      />
    </div>
  );
}
